import AddIcon from "@material-ui/icons/Add";
import {
  Box,
  Button,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@material-ui/core";
import React, { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import styled from "styled-components";
import { TContactInfo } from "../../types";
import cuideoTheme from "../../../../../../../../containers/themes/defaultTheme";
import FormSection from "../../../FormComponents/FormSection";
import ContactRelationModal from "./ContactRelationModal";
import useFormatters from "../../../../../../../../components/FormattedFields/useFormatters";
import { kinshipsList } from "../../../../../../../../util/defaults/kinship";
import ContactDetailCell from "./ContactDetailCell";
import ViewInZohoIconButton from "../../../../../../components/ViewInZohoButton/ViewInZohoIconButton";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";

const StyledTable = styled(Table)`
  table-layout: fixed;

  .MuiTableCell-head:nth-child(1) {
    width: 42.5%;
  }
  .MuiTableCell-head:nth-child(2) {
    width: 20%;
  }
  .MuiTableCell-head:nth-child(3) {
    width: 20%;
  }
  .MuiTableCell-head:nth-child(4) {
    width: 10rem;
  }

  .MuiTableCell-body {
    font-size: 0.875rem;
  }
`;

const StyledHeadCell = styled(TableCell)`
  background-color: rgba(0, 72, 120, 0.1);
  color: ${cuideoTheme.palette.primary.light};
  font-size: 0.75rem;
  line-height: normal;
  font-weight: 700;
  padding: 0.75rem 0.75rem;
`;

const StyledBodyRow = styled(TableRow)`
  background-color: #fff;

  .CMuiInTableActions {
    .MuiGrid-item:nth-child(1) {
      opacity: 0;
      transition: all 0.1s ease-in-out 0;
      transform: translateX(4px);
    }
    .MuiGrid-item:nth-child(2) {
      opacity: 0;
      transition: all 0.1s ease-in-out 0;
      transform: translateX(8px);
    }
    .MuiGrid-item:nth-child(3) {
      opacity: 0;
      transition: all 0.1s ease-in-out 0;
      transform: translateX(12px);
    }
  }

  &:hover {
    .CMuiInTableActions {
      .MuiGrid-item:nth-child(1) {
        opacity: 1;
        transition: all 0.125s ease-in-out 0.02s;
        transform: translateX(0);
      }
      .MuiGrid-item:nth-child(2) {
        opacity: 1;
        transition: all 0.125s ease-in-out 0.04s;
        transform: translateX(0);
      }
      .MuiGrid-item:nth-child(3) {
        opacity: 1;
        transition: all 0.125s ease-in-out 0.06s;
        transform: translateX(0);
      }
    }
  }
`;

const StyledOneCell = styled(TableCell)``;

const StyledOneCellMessage = styled(Typography)`
  font-size: 0.875rem;
  color: #777;
  text-align: center;
  margin: 1rem 2rem;
`;

const StyledTableBodyCell = styled(TableCell)``;

const StyledTableActions = styled(Box)`
  text-align: right;
  margin: 1rem 0;
`;

const StyledInTableActions = styled(Box)`
  text-align: right;
`;

const StyledInTableIconButton = styled(IconButton)`
  color: ${cuideoTheme.palette.primary.main};
`;

const StyledInTableIconDeleteButton = styled(IconButton)`
  color: ${cuideoTheme.palette.error.dark};
`;

const StyledButton = styled(Button)``;

interface IContactsTableProps {
  contacts: TContactInfo[];
  onContactsChange: (newContacts: TContactInfo[]) => void;
}

const ContactsTable = (props: IContactsTableProps) => {
  const { contacts, onContactsChange } = props;
  const { formatMessage } = useIntl();
  const { formatKinship } = useFormatters();
  const [state, setState] = useState({
    open: false,
    contact: null as TContactInfo | null,
  });

  const handleAddContact = () => {
    setState({ open: true, contact: null });
  };

  const handleCancel = () => {
    setState((prevState) => ({ ...prevState, open: false }));
  };

  const handleContactSelect = (contact: TContactInfo) => {
    if (contact.id) {
      onContactsChange([
        ...contacts.filter((c) => c.id !== contact.id),
        contact,
      ]);
    } else {
      onContactsChange([...contacts, contact]);
    }
    setState((prevState) => ({ ...prevState, open: false }));
  };

  const handleContactOpenEdit = (contact: TContactInfo) => {
    setState((prevState) => ({ open: true, contact }));
  };

  const handleRemoveRelationship = (contact: TContactInfo) => {
    if (
      contact.op === "existing_relation" ||
      contact.op === "modify_existing_relation"
    ) {
      onContactsChange(
        contacts.map((c) => {
          if (c.id === contact.id) {
            return {
              ...c,
              op: "existing_relation_remove",
            };
          }
          return c;
        })
      );
    } else {
      onContactsChange(contacts.filter((c) => c.id !== contact.id));
    }
  };

  const kinshipOptions = kinshipsList().map((kinship) => ({
    value: kinship.value,
    label: formatKinship(kinship.value),
  }));

  const filteredContacts = contacts.filter(
    (contact) => contact.op !== "existing_relation_remove"
  );

  return (
    <FormSection>
      <StyledTable>
        <TableHead>
          <TableRow>
            <StyledHeadCell>
              <FormattedMessage
                id="ContactsTable.FullName"
                defaultMessage="Nombre"
              />
            </StyledHeadCell>
            <StyledHeadCell>
              <FormattedMessage
                id="ContactsTable.Relationship"
                defaultMessage="Relación"
              />
            </StyledHeadCell>
            <StyledHeadCell></StyledHeadCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredContacts.length === 0 ? (
            <StyledBodyRow>
              <StyledOneCell colSpan={5}>
                <StyledOneCellMessage>
                  <FormattedMessage
                    id="ContactsTable.Empty"
                    defaultMessage="No hay ningún contacto para este usuario"
                  />
                </StyledOneCellMessage>
              </StyledOneCell>
            </StyledBodyRow>
          ) : (
            filteredContacts.map((contact, i) => (
              <StyledBodyRow key={`cr-${contact.id}-${i}`}>
                <StyledTableBodyCell>
                  <ContactDetailCell
                    First_Name={contact.First_Name}
                    Last_Name={contact.Last_Name}
                    Phone={contact.Phone}
                    Email={contact.Email}
                  />
                </StyledTableBodyCell>
                <StyledTableBodyCell>{contact.Parentesco}</StyledTableBodyCell>
                <StyledTableBodyCell>
                  <StyledInTableActions className="CMuiInTableActions">
                    <Grid
                      container
                      alignItems="center"
                      direction="row-reverse"
                      spacing={2}
                    >
                      <Grid item>
                        <Tooltip
                          title={formatMessage({
                            id: "ContactsTable.DeleteRelationship",
                            defaultMessage: "Eliminar relación",
                          })}
                        >
                          <StyledInTableIconDeleteButton
                            onClick={() => {
                              handleRemoveRelationship(contact);
                            }}
                            size="small"
                          >
                            <DeleteIcon />
                          </StyledInTableIconDeleteButton>
                        </Tooltip>
                      </Grid>
                      <Grid item>
                        <Tooltip
                          title={formatMessage({
                            id: "ContactsTable.EditRelationship",
                            defaultMessage: "Editar relación",
                          })}
                        >
                          <StyledInTableIconButton
                            onClick={() => {
                              handleContactOpenEdit(contact);
                            }}
                            size="small"
                          >
                            <EditIcon />
                          </StyledInTableIconButton>
                        </Tooltip>
                      </Grid>
                      {contact.contact_crm_id && (
                        <Grid item>
                          <Tooltip
                            title={formatMessage({
                              id: "ContactsTable.ViewContactInZoho",
                              defaultMessage: "Ver contacto en Zoho CRM",
                            })}
                          >
                            <ViewInZohoIconButton
                              zohoModule="Contact"
                              zohoId={contact.contact_crm_id}
                            />
                          </Tooltip>
                        </Grid>
                      )}
                    </Grid>
                  </StyledInTableActions>
                </StyledTableBodyCell>
              </StyledBodyRow>
            ))
          )}
        </TableBody>
      </StyledTable>
      <StyledTableActions>
        <StyledButton
          variant="contained"
          color="primary"
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddContact}
        >
          <FormattedMessage
            id="ContactsTable.Add"
            defaultMessage="Añadir contacto"
          />
        </StyledButton>
      </StyledTableActions>
      <ContactRelationModal
        open={state.open}
        onContactSelect={handleContactSelect}
        onCancel={handleCancel}
        kinshipOptions={kinshipOptions}
        contact={state.contact ?? undefined}
      />
    </FormSection>
  );
};

export default ContactsTable;
